import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import type { SessionResponse } from "../../shared/types";
import { logout } from "../lib/api";
import {
  dismissReviewEncouragement,
  hasPostedReviewLocally,
  recordReviewEncouragementInteraction,
} from "../lib/review-encouragement";

export function Layout(props: {
  session: SessionResponse["user"];
  refreshSession: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closePromptRef = useRef<HTMLButtonElement | null>(null);
  const hasLocalReviewEvidence = hasPostedReviewLocally(props.session?.id);
  const hasKnownReviewFlag = typeof props.session?.hasSubmittedReview === "boolean";
  const canEvaluateReviewPrompt = Boolean(props.session?.id) && (hasKnownReviewFlag || hasLocalReviewEvidence);
  const hasSubmittedReview = Boolean(props.session?.hasSubmittedReview) || hasLocalReviewEvidence;

  async function handleLogout() {
    setIsLoggingOut(true);
    setLogoutError(null);
    try {
      await logout();
      await props.refreshSession();
      navigate("/");
    } catch (error) {
      setLogoutError(error instanceof Error ? error.message : "Could not log out. Please try again.");
    } finally {
      setIsLoggingOut(false);
    }
  }

  useEffect(() => {
    if (!canEvaluateReviewPrompt || showReviewPrompt || hasSubmittedReview) {
      return;
    }

    function handleInteraction(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (!target.closest("a, button, input, select, textarea, label")) {
        return;
      }

      if (recordReviewEncouragementInteraction(props.session?.id)) {
        setShowReviewPrompt(true);
      }
    }

    document.addEventListener("click", handleInteraction, true);
    return () => {
      document.removeEventListener("click", handleInteraction, true);
    };
  }, [canEvaluateReviewPrompt, hasSubmittedReview, props.session?.id, showReviewPrompt]);

  useEffect(() => {
    if (!canEvaluateReviewPrompt || hasSubmittedReview) {
      setShowReviewPrompt(false);
    }
  }, [canEvaluateReviewPrompt, hasSubmittedReview, props.session?.id]);

  function handleDismissReviewPrompt() {
    dismissReviewEncouragement(props.session?.id);
    setShowReviewPrompt(false);
  }

  useEffect(() => {
    if (!showReviewPrompt) {
      return;
    }

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closePromptRef.current?.focus();

    function handleModalKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        handleDismissReviewPrompt();
        return;
      }
      if (event.key !== "Tab" || !modalRef.current) {
        return;
      }

      const focusable = [...modalRef.current.querySelectorAll<HTMLElement>(
        "button, a[href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      )].filter((element) => !element.hasAttribute("disabled"));
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleModalKeyDown);
    return () => {
      document.removeEventListener("keydown", handleModalKeyDown);
      previousFocus?.focus();
    };
  }, [showReviewPrompt]);

  return (
    <div className="shell">
      <header className="masthead">
        <Link to="/" className="brand-lockup">
          <img src="/layup-list-mark.svg?v=3" alt="Layup List" className="brand-mark" />
          <span>
            <strong>Layup List</strong>
            <small>Dartmouth’s premier course review site, relaunched.</small>
          </span>
        </Link>
        <nav className="top-nav">
          <NavLink to="/departments/">Browse</NavLink>
          <NavLink to="/search/">Search</NavLink>
          <NavLink to="/courses-by-median/">By Median</NavLink>
          <NavLink to="/professors/">Professors</NavLink>
          {props.session ? (
            <>
              <div className="session-pill">
                <strong>{props.session.email}</strong>
                <small>{props.session.isVerified ? "Verified Dartmouth account" : "Email verification pending"}</small>
              </div>
              <button type="button" className="ghost-button" onClick={handleLogout} disabled={isLoggingOut}>
                {isLoggingOut ? "Logging out..." : "Log out"}
              </button>
            </>
          ) : (
            <>
              <NavLink to="/login/">Log in</NavLink>
              <NavLink to="/signup/" className="pill-link">
                Join with Dartmouth email
              </NavLink>
            </>
          )}
        </nav>
      </header>
      {logoutError ? <p className="status-message global-status" role="alert">{logoutError}</p> : null}
      <main className="page-frame">
        <Outlet />
      </main>
      {showReviewPrompt ? (
        <div className="review-encouragement-overlay" role="presentation">
          <div ref={modalRef} className="review-encouragement-modal" role="dialog" aria-modal="true" aria-labelledby="review-encouragement-title">
            <button
              ref={closePromptRef}
              type="button"
              className="review-encouragement-close"
              onClick={handleDismissReviewPrompt}
              aria-label="Dismiss review reminder"
            >
              ×
            </button>
            <div className="section-heading">
              <p className="eyebrow">Help other students</p>
              <h2 id="review-encouragement-title">Help make course selection better</h2>
            </div>
            <p className="auth-copy">
              If you have the time, publish 1 review. It makes a huge difference for the community.
            </p>
            <p className="auth-copy">
              Publish one review to never see this pop up again.
            </p>
            <div className="hero-actions review-encouragement-actions">
              <Link to="/search/" className="primary-link" onClick={handleDismissReviewPrompt}>
                Find a course to review
              </Link>
              <button type="button" className="ghost-button" onClick={handleDismissReviewPrompt}>
                Maybe later
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <footer className="site-footer">
        <p>Made with love by a Dartmouth student for everyone.</p>
      </footer>
    </div>
  );
}
